import React from 'react';
import moment from 'moment-timezone';
import { observer } from 'mobx-react';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';

const DAYS_THRESHOLDS = {
	LOW: 45,
	MEDIUM: 60
};

const DAYS_STYLES = {
	LOW: {
		color: '#34D3B3',
		backgroundColor: '#F2FFFC'
	},
	MEDIUM: {
		color: '#F79009',
		backgroundColor: '#FFFCF5'
	},
	HIGH: {
		color: '#E31B54',
		backgroundColor: '#FFF5F6'
	}
};

const TABLE_COLUMNS = {
	JOB_POSITION: 'jobApplication.job.title',
	HIRING_MANAGER: 'jobApplication.job.hiringManager.name',
	NAME: 'jobApplication.applicant.name',
	HIRED_ON: 'closingDate',
	CLOSING_DATE: 'closingDate',
	DAYS_TO_HIRE: 'daysToHire'
};

function HiresTable({ data, sort, onSort, onSuccess }) {
	const getDaysOpen = (createdAt, closingDate) => {
		if (!createdAt) return '-';

		const start = moment(createdAt);
		const end = closingDate ? moment(closingDate) : moment();
		const days = end.diff(start, 'days');

		return days >= 0 ? days : '-';
	};

	const getDaysOpenStyle = days => {
		if (days === '-' || days < 0) return {};

		if (days <= DAYS_THRESHOLDS.LOW) {
			return DAYS_STYLES.LOW;
		}

		if (days <= DAYS_THRESHOLDS.MEDIUM) {
			return DAYS_STYLES.MEDIUM;
		}

		return DAYS_STYLES.HIGH;
	};

	const isColumnSorted = column => {
		if (!sort || sort.column !== column) return null;

		const icon = sort.asc ? 'arrow-up-icon' : 'arrow-down-icon';
		return (
			<span className="sort-icon">
				<img src={`/ats/icons/${icon}.svg`} alt="" />
			</span>
		);
	};

	const handleHeaderClick = column => {
		onSort?.(column);
	};

	const renderTableHeader = () => (
		<thead>
			<tr>
				<th
					style={{ width: '25%' }}
					className="is-sortable"
					onClick={() => handleHeaderClick(TABLE_COLUMNS.NAME)}>
					Name {isColumnSorted(TABLE_COLUMNS.NAME)}
				</th>
				<th
					style={{ width: '25%' }}
					className="is-sortable"
					onClick={() => handleHeaderClick(TABLE_COLUMNS.JOB_POSITION)}>
					Job Position {isColumnSorted(TABLE_COLUMNS.JOB_POSITION)}
				</th>
				<th
					style={{ width: '20%' }}
					className="is-sortable"
					onClick={() => handleHeaderClick(TABLE_COLUMNS.HIRING_MANAGER)}>
					Hiring Manager {isColumnSorted(TABLE_COLUMNS.HIRING_MANAGER)}
				</th>
				<th
					style={{ width: '20%' }}
					className="is-sortable"
					onClick={() => handleHeaderClick(TABLE_COLUMNS.HIRED_ON)}>
					Hired On {isColumnSorted(TABLE_COLUMNS.HIRED_ON)}
				</th>
				<th
					style={{ width: '15%' }}
					className="is-sortable"
					onClick={() => handleHeaderClick(TABLE_COLUMNS.CLOSING_DATE)}>
					Aging{' '}
					<span
						className="ml-1 tooltip is-tooltip-left"
						data-tooltip="Aging from open to hired">
						<img src="/ats/icons/tooltip-icon.svg" alt="Tooltip" />
					</span>{' '}
					{isColumnSorted(TABLE_COLUMNS.CLOSING_DATE)}
				</th>
				<th style={{ width: '13%' }}>
					DTH{' '}
					<span
						className="ml-1 tooltip is-tooltip-left"
						data-tooltip="Days from presented to hired">
						<img src="/ats/icons/tooltip-icon.svg" alt="Tooltip" />
					</span>
				</th>
			</tr>
		</thead>
	);

	const renderTableRow = row => {
		const daysOpen = getDaysOpen(row.createdAt, row.closingDate);

		return (
			<tr key={row.id}>
				<td>
					{row.jobApplication?.applicant && (
						<UserProfileAvatar
							imageUrl={
								row.jobApplication?.applicant?.imageUrl ||
								row.jobApplication?.applicant?.candidate?.imageUrl
							}
							name={row.jobApplication?.applicant?.name}
							showName
						/>
					)}
				</td>
				<td>{row.jobApplication?.job?.title}</td>
				<td>{row.jobApplication?.job?.hiringManager?.name}</td>
				<td>{moment(row.closingDate).format('ll')}</td>
				<td className="days-open-column">
					<span style={getDaysOpenStyle(daysOpen)}>{daysOpen}</span>
				</td>
				<td className="days-open-column">
					<span style={getDaysOpenStyle(row.daysToHire)}>{row.daysToHire}</span>
				</td>
			</tr>
		);
	};

	return (
		<table className="table is-fullwidth">
			{renderTableHeader()}
			<tbody>{data.map(renderTableRow)}</tbody>
		</table>
	);
}

export default observer(HiresTable);
